import { ArticleComponent } from '@wix/answers-api';
import { deserialize } from '@wix/answers-article-html-serdes';
import { ArticleViewer } from '@wix/answers-article-viewer';
import { Box, Loader, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Article as ArticleIcon } from '@wix/wix-ui-icons-common';
import React, { FC, FocusEventHandler, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { TLocalesKeys } from '../../../../types';
import { isObject } from '../../../../util/guards';
import { IArticle } from '../../../common/messageCreators';
import { useHttpClient } from '../../../common/providers/httpClient';
import { selectArticlePreviews } from '../../../common/selectors/chat';
import { useAppDispatch } from '../../../common/store';
import { loadArticlePreview } from '../../../common/thunks/articlePreview.thunk';
import { articleOpen } from '../../../messageChannel/postNewMessages';
import { logger } from '../../../util/logger';
import { renderModel } from '../../../util/renderModel';
import { useIsSmallScreen } from '../../hooks';
import { useOpenArticleFromChatbot } from '../../hooks/useOpenArticleFromChatbot';
import { ButtonLinkWrapper } from '../ButtonLinkWrapper';
import { ButtonsGrid } from '../ButtonsGrid';
import { MessageItem } from '../MessageItem';
import s from './styles.scss';

// .focus() method is available but not typed in WDS
const isTextButtonFocusable = (el: TextButton): el is TextButton & { focus: () => void } => {
  return isObject(el) && typeof el.focus === 'function';
};

const isCopilot = renderModel.isCopilot();

export const ArticlePreview: FC<IArticle> = memo(function ArticlePreviewComponent({ articleId, id }) {
  const httpClient = useHttpClient();
  const dispatch = useAppDispatch();
  const openArticleFromChatbot = useOpenArticleFromChatbot();
  const articlePreviews = useSelector(selectArticlePreviews);
  const { t } = useTranslation();
  const expandButtonRef = useRef<TextButton>(null);
  const messageItemRef = useRef<HTMLDivElement>(null);
  const isSmallScreen = useIsSmallScreen();
  const widgetId = `widget-${uuid()}`;
  const blockHeight = isSmallScreen ? '290px' : '230px';
  const { article = null, isLoading = true, error = null } = articlePreviews?.[articleId] ?? {};

  useEffect(() => {
    // if article is loaded(for example, if user opens chatbot several times) we do not need to fetch it again
    if (!articlePreviews?.[articleId]) {
      dispatch(loadArticlePreview({ id: articleId, httpClient }));
    }
  }, [articlePreviews, articleId, dispatch, httpClient]);

  const openFullArticle = useCallback(() => {
    if (isCopilot && article) {
      articleOpen(article.url);
    } else {
      article && openArticleFromChatbot(article.id);
    }
  }, [openArticleFromChatbot, article]);

  const onArticleFocusHandler: FocusEventHandler<HTMLDivElement> = useCallback((event) => {
    const expandButton = expandButtonRef.current;
    // First focusable element in the component
    const articleButton = messageItemRef.current?.querySelector('button');

    // Routing all focus events from inside the article to either expand button or the first focusable element in the component
    if (articleButton === event.relatedTarget) {
      // Means it is tab (focus is going down)
      if (expandButton && isTextButtonFocusable(expandButton)) {
        expandButton.focus();
      }
    } else {
      // Means it is shift-tab (focus is going up)
      articleButton?.focus();
    }
  }, []);

  const components = useMemo<ArticleComponent[]>(
    () => (article ? deserialize(article.content, article.locale) : null),
    [article],
  );

  if (!article && isLoading) {
    return (
      <MessageItem id={id} width={isSmallScreen ? 'standard' : 'full'}>
        <ButtonsGrid>
          <Box height={blockHeight} verticalAlign="middle" direction="vertical">
            <Loader dataHook="article__loader" />
          </Box>
        </ButtonsGrid>
      </MessageItem>
    );
  }

  if (error) {
    // TODO(bar): Add error handling (currently we show nothing)
    logger.error({
      error: new Error(`Failed to fetch Article: ${error}`),
      message: 'Error in article preview',
      callParams: { articleId },
    });
  }

  return (
    <MessageItem id={id} ref={messageItemRef} width={isSmallScreen ? 'standard' : 'full'}>
      {!!article && (
        <Box overflow="hidden" direction="vertical">
          <ButtonsGrid>
            <ButtonLinkWrapper onClick={openFullArticle} data-hook="article-preview__container">
              <Box className={s.articlePreview} direction="vertical" maxHeight={blockHeight}>
                <Box
                  direction="vertical"
                  padding="SP2 SP3 0"
                  gap="SP1"
                  borderBottomWidth="1px"
                  borderBottomStyle="solid"
                  borderBottomColor="B40"
                  minHeight={0}
                  overflow="hidden"
                >
                  <Text secondary weight="bold">
                    {article.title}
                  </Text>
                  <Box overflow="hidden">
                    <div className={s.overflowClip} onFocus={onArticleFocusHandler}>
                      <ArticleViewer components={components} locale={article.locale} theme="helpcenter" />
                    </div>
                  </Box>
                </Box>
                <Box padding="SP2 SP3">
                  <TextButton
                    ref={expandButtonRef}
                    size="medium"
                    prefixIcon={<ArticleIcon />}
                    aria-expanded="false"
                    aria-controls={widgetId}
                    dataHook="expand"
                  >
                    <Text weight="normal" skin="primary">
                      {t<TLocalesKeys>('article_btn_label')}
                    </Text>
                  </TextButton>
                </Box>
              </Box>
            </ButtonLinkWrapper>
          </ButtonsGrid>
        </Box>
      )}
    </MessageItem>
  );
});
